'use client';

import { useMemo } from 'react';
import { getEventSlug } from '@/lib/event-slugs';
import type { CalendarEvent, LensId } from '@/lib/types';
import type { LongWeekend } from '@/lib/long-weekends';
import { formatDateWithDay, formatMonthName } from '@/lib/date-format';
import { getEventColor } from '@/lib/constants';
import { uiFb } from '@/lib/i18n';
import { VacationList } from './LandingShowcase';

export function groupByMonth(events: CalendarEvent[]): Record<number, CalendarEvent[]> {
  const map: Record<number, CalendarEvent[]> = {};
  for (const event of events) {
    const month = parseInt(event.dateStart.slice(5, 7), 10) - 1;
    if (!map[month]) map[month] = [];
    map[month].push(event);
  }
  return map;
}

interface ListShowcaseProps {
  events: CalendarEvent[];
  year: number;
  entityLabel: string;
  entityIcon: string;
  lens?: LensId;
  lang?: string;
  longWeekends?: LongWeekend[] | null;
  /** When true, omit the outer <section> wrapper and header (used by hybrid view) */
  bare?: boolean;
  /**
   * When true, keep the <section> wrapper but omit the title heading — used when
   * a parent (ContentCalendarView) already renders the page H1, so the showcase
   * must not repeat it as an H2 and break the heading outline. Refs: almanak-bzfs.
   */
  hideHeader?: boolean;
}

/** Month-grouped event list with semantic HTML for SEO snippet eligibility */
export function ListMonthGrid({ events, lang = 'en' }: { events: CalendarEvent[]; lang?: string }) {
  const byMonth = groupByMonth(events);

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
      {Array.from({ length: 12 }, (_, m) => {
        const monthEvents = byMonth[m] || [];
        if (monthEvents.length === 0) return null;

        return (
          <div
            key={m}
            className="rounded-xl border border-border-subtle bg-surface-card p-3 md:p-4"
          >
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary md:text-sm">
              {formatMonthName(m, lang)}
            </h3>
            <ul className="space-y-1.5">
              {monthEvents.map((event) => (
                <li key={event.id} className="flex items-start gap-2 text-xs text-text-primary md:text-sm">
                  <span
                    className="mt-1.5 block h-2 w-2 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: getEventColor(event.type) }}
                  />
                  <span>
                    <a href={`/events/${getEventSlug(event.id)}`} className="font-medium hover:underline">
                      {event.title}
                    </a>
                    <span className="ml-1 text-text-dim">
                      {formatDateWithDay(event.dateStart, lang)}
                      {event.dateEnd !== event.dateStart && ` – ${formatDateWithDay(event.dateEnd, lang)}`}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export function ListShowcase({ events, year, entityLabel, entityIcon, lens = 'country', lang = 'en', longWeekends, bare, hideHeader }: ListShowcaseProps) {
  if (bare) {
    return (
      <>
        <ListMonthGrid events={events} lang={lang} />
        {longWeekends && longWeekends.length > 0 && (
          <VacationList longWeekends={longWeekends} lang={lang} />
        )}
      </>
    );
  }

  // Academic pages span two calendar years — use the academic-year range (almanak-m465).
  const calendarHeading = lens === 'academic'
    ? uiFb('ui.calendar.academic_calendar', 'Academic Calendar {range}', lang, { range: `${year}/${year + 1}` })
    : uiFb('ui.misc.complete_calendar', 'Complete Calendar {year}', lang, { year });

  return (
    <section id="calendar" className="scroll-mt-20 px-4 py-10 md:px-8 md:py-16 lg:px-16 xl:px-20">
      <div className="mx-auto w-full max-w-6xl">
        {!hideHeader && (
          <div className="mb-8 text-center">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-text-primary md:text-4xl">
              {entityIcon} {entityLabel} — {calendarHeading}
            </h2>
            <p className="mt-2 text-sm text-text-secondary md:text-base">
              {uiFb('ui.event.summary', '{count} events', lang, { count: events.length })}
            </p>
          </div>
        )}

        <ListMonthGrid events={events} lang={lang} />

        {longWeekends && longWeekends.length > 0 && (
          <VacationList longWeekends={longWeekends} lang={lang} />
        )}
      </div>
    </section>
  );
}
